import * as React from 'react';
import { Separator } from '@/hen-ui/atomics/Separator';
import { Text } from '@/hen-ui/atomics/Text';
import { Flex } from '@/hen-ui/composites/Flex';
import { cn } from '@/hen-ui/utils';

export interface SimpleDataPointProps {
  /** Data point title/label (required, bold text). */
  title: React.ReactNode;
  /** Data value to display (required, bold). */
  value: React.ReactNode;
  /** Optional description text below title. */
  description?: React.ReactNode;
  /** Whether to show white background container with padding and border radius. */
  background?: boolean;
  /** Whether to show bottom border divider (mutually exclusive with background). */
  rowDivider?: boolean;
  /** Optional className for the container. */
  className?: string;
}

/**
 * SimpleDataPoint - Composite component for displaying a labeled value.
 *
 * Renders a title/description block on the left with a value on the right.
 * Supports optional background container or row divider styling.
 */
export const SimpleDataPoint: React.FC<SimpleDataPointProps> = ({
  title,
  value,
  description,
  background = false,
  rowDivider = false,
  className,
}) => {
  const showBackground = background;
  const showRowDivider = !showBackground && rowDivider;

  return (
    <Flex
      direction='column'
      gap='xxs'
      className={cn(showBackground && 'bg-white p-4 rounded-md', className)}
    >
      <Flex
        direction='row'
        align='start'
        justify='between'
        gap='xxs'
        className={cn(showRowDivider && 'pb-2')}
      >
        <Flex direction='column' gap='xxs' className='gap-1'>
          <Text variant='b4' color='heading' className='font-bold'>
            {title}
          </Text>
          {description && (
            <Text variant='l1' color='default'>
              {description}
            </Text>
          )}
        </Flex>
        <Text
          variant='b2'
          color='heading'
          className='font-bold text-right shrink-0'
        >
          {value}
        </Text>
      </Flex>
      {showRowDivider && <Separator />}
    </Flex>
  );
};

SimpleDataPoint.displayName = 'SimpleDataPoint';
